import { DataTable, DataTableProps } from 'common/components/DataTable';
import { SyncTaskNotification } from 'common/models/databaseSync/syncNotification';
import { DatabaseSyncStatus } from 'common/models/databaseSync/syncStatus';
import { SyncTaskStatus } from 'common/models/databaseSync/syncTaskStatus';
import { taskNames } from 'features/settings-view/components/Sub-views/SpokaneSyncView/utils/taskNames';
import { taskStatusNames } from 'features/settings-view/components/Sub-views/SpokaneSyncView/utils/taskStatusNames';
import { useSyncTableData } from 'features/settings-view/hooks/useSyncTableData';
import { FC, useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { SeasonToSyncFilter } from './SeasonToSyncFilter';
import {
  ErrorTableStyledWrapper,
  SeasonFilterAndBtnWrapper,
  SyncProgressBar,
  TitleCase,
} from './styles';
import { SyncButtonAndProgress } from './SyncButtonAndProgress';
import { SyncErrorsDetails } from './SyncErrorsDetails';

/**
 * Allows the user to start the Spokane synchronization process and displays
 * information sent by the server as the process progresses and completes.
 */
export const TaskResultsTable: FC = () => {
  const {
    taskResults,
    onSyncClick,
    waitingToStart,
    syncProgress,
    syncNotification,
    syncStatusError,
    syncErrorMessage,
    syncEstimateErrors,
    startingLocally,
  } = useSyncTableData();
  const [showModal, setShowModal] = useState(false);

  const taskDetailsPresent =
    !!taskResults.length || !!syncEstimateErrors?.length;
  const syncInProgress = syncNotification.status === DatabaseSyncStatus.Pending;

  const columns: Column<SyncTaskNotification>[] = [
    {
      accessor: 'descriptor',
      Header: 'Sync Task',
      width: '25%',
      Cell: ({ value }) => <TitleCase>{taskNames[value]}</TitleCase>,
    },
    {
      accessor: 'status',
      Header: 'Status',
      width: '20%',
      Cell: ({ value }) => (
        <TitleCase isFailed={value === SyncTaskStatus.Failed}>
          {[taskStatusNames[value]]}
        </TitleCase>
      ),
    },
    {
      accessor: 'error',
      Header: 'Message',
      width: '55%',
      Cell: ({ value }) => <span>{value}</span>,
    },
  ];

  const Header = useCallback(
    () => (
      <>
        <SeasonFilterAndBtnWrapper>
          <SeasonToSyncFilter
            disabled={syncStatusError || startingLocally || syncInProgress}
            showModal={showModal}
            setShowModal={setShowModal}
          />
          <SyncButtonAndProgress
            syncStatusError={syncStatusError}
            onSyncClick={onSyncClick}
            syncNotification={syncNotification}
            syncInProgress={syncInProgress}
            startingLocally={startingLocally}
            showModal={showModal}
          />
        </SeasonFilterAndBtnWrapper>
        <SyncProgressBar
          variant={waitingToStart ? 'indeterminate' : 'determinate'}
          value={syncProgress}
        />
      </>
    ),
    [
      onSyncClick,
      showModal,
      startingLocally,
      syncInProgress,
      syncNotification,
      syncProgress,
      syncStatusError,
      waitingToStart,
    ],
  );

  const syncErrorProps = useMemo(
    () => ({
      syncErrorMessage,
      syncEstimateErrors,
      taskDetailsPresent,
    }),
    [syncErrorMessage, syncEstimateErrors, taskDetailsPresent],
  );

  const tableProps: DataTableProps<SyncTaskNotification> = {
    toolBar: Header(),
    isLoading: false,
    isFetching: false,
    columns,
    data: taskResults,
    FooterComponent: () => <SyncErrorsDetails {...syncErrorProps} />,
  };

  return (
    <ErrorTableStyledWrapper>
      <DataTable<SyncTaskNotification> {...tableProps} />
    </ErrorTableStyledWrapper>
  );
};
